import { useState } from "react";
import { OrganizationTreeView } from "../components/OrganizationTreeView";
import { OrganizationFormDialog } from "../components/OrganizationFormDialog";
import { MembershipManager } from "../components/MembershipManager";
import { InvitationManager } from "../components/InvitationManager";
import { isApiError, useAddChildOrganization, useArchiveOrganization } from "../api";
import type { Organization } from "../types";
import { PageBanner } from "@/shared/components/PageBanner";

type SidePanelTab = "memberships" | "invitations";

const ARCHIVED_STATUS: Organization["status"] = "archived";

export function OrganizationHierarchyPage() {
  const [selected, setSelected] = useState<Organization | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [tab, setTab] = useState<SidePanelTab>("memberships");
  const [reparentTargetId, setReparentTargetId] = useState("");

  const archiveOrg = useArchiveOrganization();
  const addChild = useAddChildOrganization(selected?.id ?? "");
  const isArchived = selected?.status === ARCHIVED_STATUS;

  const handleReparent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !reparentTargetId || isArchived) return;
    addChild.mutate(reparentTargetId, { onSuccess: () => setReparentTargetId("") });
  };

  const archiveErrorMessage = isApiError(archiveOrg.error)
    ? String(archiveOrg.error.response.data.detail ?? "")
    : undefined;
  const addChildErrorMessage = isApiError(addChild.error)
    ? String(addChild.error.response.data.detail ?? "")
    : undefined;

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="سلسله‌مراتب سازمانی"
        subtitle="مدیریت سازمان‌ها، شرکت‌ها، و زیرمجموعه‌ها در سامانه"
        icon="🏢"
        helpLink="/help/organization/hierarchy"
      >
        <button
          onClick={() => setFormOpen(true)}
          style={{
            padding: "0.6rem 1.2rem",
            borderRadius: "12px",
            background: "#ffffff",
            color: "#007270",
            fontWeight: "700",
            fontSize: "0.9rem",
            border: "1px solid rgba(255, 255, 255, 0.8)",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            transition: "all 0.2s ease",
          }}
        >
          <span>➕</span>
          <span>سازمان جدید</span>
        </button>
      </PageBanner>

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "1.5rem", alignItems: "start" }}>
        {/* Left Side: Tree View (1 col) */}
        <div 
          className="brand-card-extand"
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.05)",
            padding: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            minHeight: "500px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "0.5rem", borderBottom: "1px solid #eee" }}>
            <h2 style={{ fontWeight: 600, fontSize: "1.125rem", color: "#1f2937", margin: 0 }}>ساختار درختی</h2>
          </div>
          <OrganizationTreeView selectedId={selected?.id ?? null} onSelect={setSelected} />
        </div>

        {/* Right Side: Details & Actions (2 cols) */}
        <div 
          className="brand-card-extand"
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.05)",
            padding: "1.5rem",
            display: "flex",
            flexDirection: "column",
            minHeight: "500px"
          }}
        >
          {!selected ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
              <span style={{ fontSize: "2.25rem", marginBottom: "1rem", opacity: 0.5 }}>📋</span>
              <p>یک سازمان را از درخت سمت راست انتخاب کنید تا جزئیات آن اینجا نمایش داده شود.</p>
            </div>
          ) : (
            <>
              {/* Header Info */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", paddingBottom: "1rem", borderBottom: "1px solid #eee", marginBottom: "1.5rem" }}>
                <div>
                  <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1f2937", marginBottom: "0.5rem", marginTop: 0 }}>{selected.name}</h2>
                  <div style={{ display: "flex", gap: "1rem", fontSize: "0.875rem", color: "#6b7280" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      <span style={{ opacity: 0.7 }}>کد:</span> <span style={{ fontWeight: 500, color: "#374151" }}>{selected.code}</span>
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      <span style={{ opacity: 0.7 }}>نوع:</span> <span style={{ fontWeight: 500, color: "#374151" }}>{selected.organization_type}</span>
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      <span style={{ opacity: 0.7 }}>وضعیت:</span>
                      <span style={{ 
                        padding: "0.125rem 0.5rem", 
                        borderRadius: "0.25rem", 
                        fontSize: "0.75rem",
                        backgroundColor: isArchived ? "#fee2e2" : "#dcfce3",
                        color: isArchived ? "#b91c1c" : "#15803d"
                      }}>
                        {selected.status}
                      </span>
                    </span>
                  </div>
                </div>
                {!isArchived && (
                  <button
                    type="button"
                    onClick={() => archiveOrg.mutate(selected.id)}
                    style={{
                      fontSize: "0.875rem",
                      padding: "0.375rem 0.75rem",
                      borderRadius: "0.5rem",
                      border: "1px solid #fecaca",
                      color: "#dc2626",
                      backgroundColor: "transparent",
                      cursor: "pointer"
                    }}
                  >
                    آرشیو سازمان
                  </button>
                )}
              </div>

              {archiveOrg.isError && (
                <div style={{ padding: "0.75rem", marginBottom: "1.5rem", backgroundColor: "#fef2f2", border: "1px solid #fee2e2", color: "#dc2626", borderRadius: "0.5rem", fontSize: "0.875rem" }}>
                  {archiveErrorMessage || "آرشیو ناموفق بود — احتمالاً این سازمان زیرمجموعهٔ فعال دارد."}
                </div>
              )}

              {/* Hierarchy Management */}
              <div style={{ marginBottom: "2rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#1f2937", marginBottom: "0.75rem", marginTop: 0 }}>مدیریت زیرمجموعه‌ها</h3>
                {isArchived ? (
                  <p style={{ fontSize: "0.875rem", color: "#6b7280", padding: "0.75rem", backgroundColor: "#f9fafb", borderRadius: "0.5rem", border: "1px solid #e5e7eb", margin: 0 }}>
                    این سازمان آرشیو شده است — افزودن زیرمجموعه ممکن نیست.
                  </p>
                ) : (
                  <>
                    <form onSubmit={handleReparent} style={{ display: "flex", gap: "0.75rem" }}>
                      <input
                        value={reparentTargetId}
                        onChange={(e) => setReparentTargetId(e.target.value)}
                        placeholder="شناسهٔ سازمان (UUID) را وارد کنید..."
                        style={{ flex: 1, padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "0.5rem", fontSize: "0.875rem" }}
                      />
                      <button
                        type="submit"
                        disabled={addChild.isPending}
                        style={{
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          backgroundColor: "#1f2937",
                          color: "#ffffff",
                          fontSize: "0.875rem",
                          border: "none",
                          cursor: addChild.isPending ? "not-allowed" : "pointer",
                          opacity: addChild.isPending ? 0.5 : 1
                        }}
                      >
                        افزودن به زیرمجموعه
                      </button>
                    </form>
                    {addChild.isError && (
                      <p style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "#ef4444", marginBottom: 0 }}>
                        {addChildErrorMessage || "این عملیات ناموفق بود — یا دسترسی کافی ندارید، یا سازمان مقصد آرشیو شده است."}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Tabs */}
              <div style={{ display: "flex", gap: "1.5rem", borderBottom: "1px solid #e5e7eb", marginBottom: "1.5rem" }}>
                <button
                  type="button"
                  style={{
                    paddingBottom: "0.75rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: tab === "memberships" ? "#2563eb" : "#6b7280",
                    border: "none",
                    background: "none",
                    borderBottom: tab === "memberships" ? "2px solid #2563eb" : "2px solid transparent",
                    cursor: "pointer",
                    marginBottom: "-1px"
                  }}
                  onClick={() => setTab("memberships")}
                >
                  اعضای سازمان
                </button>
                <button
                  type="button"
                  style={{
                    paddingBottom: "0.75rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: tab === "invitations" ? "#2563eb" : "#6b7280",
                    border: "none",
                    background: "none",
                    borderBottom: tab === "invitations" ? "2px solid #2563eb" : "2px solid transparent",
                    cursor: "pointer",
                    marginBottom: "-1px"
                  }}
                  onClick={() => setTab("invitations")}
                >
                  دعوت‌نامه‌ها
                </button>
              </div>

              {/* Tab Content */}
              <div style={{ flex: 1 }}>
                {tab === "memberships" && (
                  <MembershipManager organizationId={selected.id} organizationArchived={isArchived} />
                )}
                {tab === "invitations" && (
                  <InvitationManager organizationId={selected.id} organizationArchived={isArchived} />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <OrganizationFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
